import React, { useState } from "react";
import {
  Plus,
  MessageCircle,
  Trash2,
  Search,
  Sliders,
  User,
} from "lucide-react";
import { useAppContext } from "../context/AppContext";

interface ServicesTabProps {
  displayedServices?: any[];
  serviceTab?: "local" | "global";
  setServiceTab?: (tab: "local" | "global") => void;
  userCoords?: [number, number] | null;
  setSelectedChatUser?: (user: any) => void;
  handleContactService?: (svc: any) => void;
  handleReport?: (
    contentType: string,
    contentId: string,
    reportedUserId: string
  ) => void;
  setShowAddServiceForm?: (show: boolean) => void;
  chatEndRef?: React.RefObject<HTMLDivElement>;
  [key: string]: any;
}

export default function ServicesTab({
  displayedServices = [],
  handleContactService,
  setShowAddServiceForm,
  handleReport,
}: ServicesTabProps) {
  const { user } = useAppContext();

  // Стейт для тексту пошуку
  const [searchQuery, setSearchQuery] = useState("");

  // Фільтрація послуг за введеним текстом (шукає в заголовку та описі без урахування регістру)
  const filteredServices = displayedServices.filter((service: any) => {
    const titleMatch = service.title
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    const descMatch = service.description
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    return titleMatch || descMatch;
  });

  return (
    <div className="flex-1 overflow-y-auto px-4 pt-4 pb-32 no-scrollbar bg-[var(--bg-main)] flex flex-col gap-5">
      {/* Рядок пошуку послуг */}
      <div className="relative flex items-center bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl px-3.5 py-2.5 shadow-sm">
        <Search size={18} className="text-[var(--text-muted)] mr-2 shrink-0" />
        <input
          type="text"
          placeholder="Пошук послуг (наприклад: Погуляти)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-transparent outline-none text-sm text-[var(--text-main)] placeholder-[var(--text-muted)]"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="text-xs text-[var(--text-muted)] hover:text-[var(--text-main)] ml-2 transition-colors"
          >
            Очистити
          </button>
        )}
      </div>

      {/* Заголовок та кнопка створення послуги */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-main)]">
            🐾 Послуги
          </h2>
          <p className="text-xs text-[var(--text-muted)]">
            Допомога у твоєму районі
          </p>
        </div>
        <button
          onClick={() => setShowAddServiceForm && setShowAddServiceForm(true)}
          className="flex items-center gap-1 px-4 py-2 bg-[#a073e7] text-white rounded-full font-bold text-xs shadow-md hover:bg-[#8e5fd4] active:scale-95 transition-all"
        >
          <Plus size={14} /> Створити
        </button>
      </div>

      {/* Список послуг */}
      <div className="flex flex-col gap-4">
        {filteredServices.length === 0 ? (
          <div className="text-center py-16 text-[var(--text-muted)] flex flex-col items-center gap-3">
            <div className="p-4 bg-[var(--bg-soft)] rounded-full text-[var(--text-muted)]">
              <Sliders size={32} />
            </div>
            <p className="text-sm font-medium">
              {searchQuery
                ? "Нічого не знайдено за цим запитом."
                : "У цьому радіусі послуг поки немає."}
            </p>
          </div>
        ) : (
          filteredServices.map((service: any) => (
            <div
              key={service.id}
              className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl p-4 shadow-sm flex flex-col gap-3 transition-all hover:border-[var(--border-accent)]"
            >
              {/* Шапка картки: Назва та Ціна зі знаком ₴ */}
              <div className="flex justify-between items-start gap-3">
                <h3 className="font-bold text-base text-[var(--text-main)] leading-snug break-words max-w-[70%]">
                  {service.title}
                </h3>
                <span className="bg-[var(--accent-soft)] text-[#a073e7] font-extrabold text-sm px-3 py-1 rounded-xl whitespace-nowrap shadow-sm">
                  {service.price} ₴
                </span>
              </div>

              {/* Опис послуги */}
              {service.description && (
                <p className="text-sm text-[var(--text-main)] bg-[var(--bg-soft)] p-3 rounded-xl break-words leading-relaxed">
                  {service.description}
                </p>
              )}

              {/* Підвал картки: Автор та Дії */}
              <div className="flex justify-between items-center mt-1 pt-3 border-t border-[var(--border-color)] text-xs text-[var(--text-muted)]">
                <div className="flex items-center gap-1.5 font-medium">
                  <div className="w-5 h-5 rounded-full bg-[var(--bg-soft)] flex items-center justify-center text-[var(--text-muted)]">
                    <User size={12} />
                  </div>
                  <span>
                    {service.authorName || service.author || "Власник хвостика"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Написати автору */}
                  {handleContactService &&
                    user &&
                    service.userId !== user.uid && (
                      <button
                        onClick={() => handleContactService(service)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[#a073e7] font-bold rounded-xl hover:bg-[var(--accent-soft)] transition-colors"
                      >
                        <MessageCircle size={13} />
                        Написати
                      </button>
                    )}

                  {/* Кнопка репорту */}
                  {handleReport && user && service.userId !== user.uid && (
                    <button
                      onClick={() =>
                        handleReport(
                          "service",
                          String(service.id),
                          service.userId || ""
                        )
                      }
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--red-text)] px-1"
                    >
                      Скарга
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
